import { type SceneDocument } from './document';
import { type Entity } from './entities';
import { type EntityId } from './ids';

/**
 * How a construction is presented to a class.
 *
 * ## Why none of this is stored in the document
 *
 * Every value here describes *this view, right now*: which step the replay is paused on,
 * whether helper objects are showing, whether the audience is allowed to edit. A worksheet
 * emailed to thirty students must not arrive half-revealed because the teacher left the
 * replay paused, and the same file opened by the teacher and by a student is the same file.
 *
 * The rule that follows: nothing in this module writes to a `SceneDocument`. It computes a
 * *filter* over one.
 */

export const LessonMode = {
  /** The full editor. */
  Teacher: 'teacher',
  /** Explore and measure, but do not restructure. */
  Student: 'student',
  /** Canvas only, for projecting. */
  Presentation: 'presentation',
} as const;
export type LessonMode = (typeof LessonMode)[keyof typeof LessonMode];

export interface LessonView {
  readonly mode: LessonMode;
  /** False hides helper geometry — the scaffolding, not the result. */
  readonly showConstruction: boolean;
  /**
   * How many construction steps are revealed. `null` means all of them.
   *
   * A step is one entity, in the order the entities were created. That is not an
   * approximation of what the user did — it *is* what they did, because an entity cannot be
   * created before the things it is built from. Replaying by history instead would be
   * finer-grained but would not survive a reload, and a lesson is usually replayed from a
   * saved file rather than from the session that made it.
   */
  readonly revealed: number | null;
}

export const FULL_VIEW: LessonView = Object.freeze({
  mode: LessonMode.Teacher,
  showConstruction: true,
  revealed: null,
});

/** Whether the audience may change the structure of the drawing. */
export function canRestructure(mode: LessonMode): boolean {
  return mode === LessonMode.Teacher;
}

/** Whether the editing panels are on screen. */
export function showsPanels(mode: LessonMode): boolean {
  return mode !== LessonMode.Presentation;
}

/**
 * The entities a replay reveals, in order.
 *
 * Creation order, which the entity map preserves and the codec round-trips. Derived
 * entities necessarily follow their parents, so the sequence is always a valid construction
 * whatever point it is paused at — there is no step at which something is drawn resting on
 * nothing.
 */
export function constructionSteps(document: SceneDocument): readonly EntityId[] {
  return [...document.entities.keys()];
}

/**
 * A predicate for the renderer, given a view.
 *
 * Returned as a function rather than a filtered document so that nothing downstream has to
 * distinguish "hidden by the lesson" from "deleted". The scene, the history and the object
 * list all stay whole; only the picture changes.
 */
export function visibilityFor(
  document: SceneDocument,
  view: LessonView
): (entity: Entity) => boolean {
  const revealed =
    view.revealed === null
      ? null
      : new Set(constructionSteps(document).slice(0, Math.max(0, view.revealed)));

  return (entity) => {
    if (revealed !== null && !revealed.has(entity.id)) return false;

    if (!view.showConstruction && isHelper(entity)) return false;

    return true;
  };
}

/**
 * Whether an entity is scaffolding rather than the result.
 *
 * **The mark, and nothing else — never a guess.**
 *
 * The first version guessed: an unlabelled *derived* entity was treated as scaffolding, so
 * that the button would do something on a drawing where nobody had set the flag. The guess
 * was badly wrong. Almost everything in a geometry construction is derived — a segment
 * between two points is derived — so it hid the user's actual drawing and left the points
 * behind. A test caught it, but the lesson is the general one: a heuristic that hides the
 * wrong thing is worse than a control that plainly does nothing.
 *
 * So the flag is honoured alone, and the layers panel makes it settable. Usually it is the
 * user who sets it. A tool may set it on something it made *as* scaffolding — the invisible
 * point a toolbar solid hangs from is the one case — and that is not a guess: the tool is
 * the thing that knows, and it is saying so at the moment it knows it. What stays forbidden
 * is anything downstream inferring the mark from an entity's shape.
 */
function isHelper(entity: Entity): boolean {
  return entity.isConstruction;
}
